import { Injectable } from '@angular/core';
import { map, of } from 'rxjs';

import {
  DeleteUserGQL,
  GetEmailToVerifyGQL,
  InviteUsersGQL,
  InviteUsersMutationVariables,
  LoginAfterResetGQL,
  ResetPasswordGQL,
  SaveUserCredsGQL,
  SaveUserDetailsGQL,
  SaveUserDetailsMutationVariables,
  UsersGQL,
  VerifyEmailGQL,
  VerifyEmailMutationVariables
} from '@cpd-app/generated';
import { CreateAccountInput } from '@cpd-app/core-module/models';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private usersGQL: UsersGQL,
    private saveUserDetailsGQL: SaveUserDetailsGQL,
    private inviteUsersGQL: InviteUsersGQL,
    private deleteUserGQL: DeleteUserGQL,
    private verifyEmailGQL: VerifyEmailGQL,
    private resetPasswordGQL: ResetPasswordGQL,
    private saveUserCredsGQL: SaveUserCredsGQL,
    private loginAfterResetGQL: LoginAfterResetGQL,
    private getEmailToVerifyGQL: GetEmailToVerifyGQL
  ) {}

  /**
   * ANALYTICS.22 -- reports what actually went wrong.
   *
   * This was `map((result) => result.data.users)`. The Apollo client runs with
   * `errorPolicy: 'all'`, which RESOLVES a failed query with `data: null` and
   * the errors alongside -- so a server error became
   * "Cannot read properties of null", thrown from a `.pipe`, caught by an
   * effect that swallowed it. The screen said nothing and the console said
   * nothing about the actual cause.
   *
   * Reading `errors` first turns that into the message the server sent.
   */
  public getUsers() {
    return this.usersGQL.fetch().pipe(
      map((result) => {
        if (result.errors?.length) {
          throw new Error(
            result.errors.map((err) => err.message).join('; ')
          );
        }

        // Null data with no errors should not happen, but an empty list is a
        // far better answer than a null dereference two layers up.
        return result.data?.users ?? [];
      })
    );
  }

  public getEmailToVerify(token: string) {
    try {
      return this.getEmailToVerifyGQL
        .fetch({ token })
        .pipe(map((result) => result?.data?.getEmailToVerify || ''));
    } catch (error) {
      return of('');
    }
  }

  public saveUserDetails(input: SaveUserDetailsMutationVariables) {
    return this.saveUserDetailsGQL
      .mutate(input)
      .pipe(map((result) => result.data.saveUserDetails));
  }

  public inviteUsers(input: InviteUsersMutationVariables) {
    return this.inviteUsersGQL
      .mutate(input)
      .pipe(map((result) => result.data.inviteUsers));
  }

  public deleteUser(userId: number) {
    return this.deleteUserGQL
      .mutate({ userId })
      .pipe(map((result) => result.data.deleteUser));
  }

  public verifyEmail(input: VerifyEmailMutationVariables) {
    return this.verifyEmailGQL
      .mutate(input)
      .pipe(map((result) => result.data.verifyEmail));
  }

  public resetPassword(email: string) {
    return this.resetPasswordGQL
      .mutate({ email })
      .pipe(map((result) => result.data.resetPassword));
  }

  public changePassword(token: string, password: string) {
    return this.loginAfterResetGQL
      .mutate({ token, password })
      .pipe(map((result) => result.data.loginAfterReset));
  }

  public saveUserCreds(input: CreateAccountInput) {
    return this.saveUserCredsGQL
      .mutate({
        email: input.email,
        password: input.password,
        token: input.token
      })
      .pipe(map((result) => result.data.saveUserCreds));
  }
}
